import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Shield, CheckCircle, AlertTriangle, FileText, Loader2 } from 'lucide-react';
import { policyAcknowledgmentService } from '@/services/policyAcknowledgmentService';
import { PolicyDocumentViewer } from '@/components/legal/PolicyDocumentViewer';
import { useToast } from '@/hooks/use-toast';

const VerifyParentalConsent = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const token = searchParams.get('token');

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  
  const [consentChecked, setConsentChecked] = useState(false);
  const [privacyChecked, setPrivacyChecked] = useState(false);
  const [tosChecked, setTosChecked] = useState(false);
  
  const [viewingPolicy, setViewingPolicy] = useState<'privacy' | 'tos' | null>(null);

  useEffect(() => {
    // Validate that we have a token
    if (!token) {
      setError('Invalid verification link. Please check the link in your email.');
      setIsLoading(false);
      return;
    }
    
    setIsLoading(false);
  }, [token]);

  const allChecked = consentChecked && privacyChecked && tosChecked;

  const handleSubmit = async () => {
    if (!allChecked || !token) return;

    setIsSubmitting(true);
    try {
      const result = await policyAcknowledgmentService.verifyParentalConsent(token);

      if (result.success) {
        setSuccess(true);
        toast({
          title: "Consent Verified!",
          description: "Your child can now fully access Players Lockeroom.",
        });
      } else {
        setError(result.error as string || 'Verification failed. Please try again.');
        toast({
          title: "Verification Failed",
          description: result.error as string || 'Please try again or request a new verification link.',
          variant: "destructive"
        });
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Validating verification link...</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center">
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
            <CardTitle className="text-2xl font-bold text-green-500">
              Consent Verified!
            </CardTitle>
            <CardDescription>
              Thank you for verifying your parental consent. Your child can now fully access Players Lockeroom.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-sm text-muted-foreground mb-6">
              As a parent/guardian, you can manage your child's account at any time through the Parental Settings page.
            </p>
            <Button onClick={() => navigate('/')} className="w-full">
              Close This Page
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error && !token) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center">
              <AlertTriangle className="h-8 w-8 text-destructive" />
            </div>
            <CardTitle className="text-2xl font-bold text-destructive">
              Invalid Link
            </CardTitle>
            <CardDescription>
              {error}
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-sm text-muted-foreground mb-6">
              Please check that you clicked the correct link from your email, or ask your child to resend the verification email.
            </p>
            <Button variant="outline" onClick={() => navigate('/')}>
              Go to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-2xl w-full">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
              <Shield className="h-8 w-8 text-primary" />
            </div>
            <CardTitle className="text-2xl font-bold">
              Parental Consent Verification
            </CardTitle>
            <CardDescription>
              Your child has requested to use Players Lockeroom. Please review the information below and provide your consent.
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {/* COPPA Notice */}
            <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
              <h3 className="font-semibold text-foreground mb-2">About COPPA Compliance</h3>
              <p className="text-sm text-muted-foreground">
                Under the Children's Online Privacy Protection Act (COPPA), websites must obtain verifiable 
                parental consent before collecting personal information from children under 13. By verifying 
                your consent below, you agree to allow your child to use Players Lockeroom and consent to 
                the collection and use of their information as described in our policies.
              </p>
            </div>

            {/* What We Collect */}
            <div className="space-y-2">
              <h3 className="font-semibold text-foreground">What We Collect From Your Child</h3>
              <ul className="text-sm text-muted-foreground space-y-1 ml-4 list-disc">
                <li>Username and profile information</li>
                <li>Team affiliations and game participation data</li>
                <li>Communications with coaches and team members</li>
                <li>Performance statistics and achievements</li>
              </ul>
            </div>

            {/* Your Rights */}
            <div className="space-y-2">
              <h3 className="font-semibold text-foreground">Your Rights as a Parent/Guardian</h3>
              <ul className="text-sm text-muted-foreground space-y-1 ml-4 list-disc">
                <li>Review your child's personal information at any time</li>
                <li>Request deletion of your child's account and data</li>
                <li>Refuse further collection of your child's information</li>
                <li>Revoke this consent at any time</li>
              </ul>
            </div>

            {error && (
              <div className="p-4 bg-destructive/10 border border-destructive/30 rounded-lg">
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}

            {/* Consent Checkboxes */}
            <ScrollArea className="max-h-[200px]">
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg border border-border">
                  <Checkbox
                    id="privacy-policy"
                    checked={privacyChecked}
                    onCheckedChange={(checked) => setPrivacyChecked(checked as boolean)}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <label htmlFor="privacy-policy" className="text-sm font-medium text-foreground cursor-pointer flex items-center gap-2">
                      <FileText className="h-4 w-4 text-primary" />
                      Privacy Policy
                    </label>
                    <p className="text-xs text-muted-foreground mt-1">
                      I have read and agree to the{' '}
                      <button
                        type="button"
                        onClick={() => setViewingPolicy('privacy')}
                        className="text-primary hover:underline"
                      >
                        Privacy Policy
                      </button>
                      , including how my child's personal data will be collected, used, and protected.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg border border-border">
                  <Checkbox
                    id="terms-of-service"
                    checked={tosChecked}
                    onCheckedChange={(checked) => setTosChecked(checked as boolean)}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <label htmlFor="terms-of-service" className="text-sm font-medium text-foreground cursor-pointer flex items-center gap-2">
                      <FileText className="h-4 w-4 text-primary" />
                      Terms of Service
                    </label>
                    <p className="text-xs text-muted-foreground mt-1">
                      I have read and agree to the{' '}
                      <button
                        type="button"
                        onClick={() => setViewingPolicy('tos')}
                        className="text-primary hover:underline"
                      >
                        Terms of Service
                      </button>
                      , including the rules for using the platform.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 bg-primary/5 rounded-lg border border-primary/30">
                  <Checkbox
                    id="parental-consent"
                    checked={consentChecked}
                    onCheckedChange={(checked) => setConsentChecked(checked as boolean)}
                    className="mt-1"
                    disabled={!privacyChecked || !tosChecked}
                  />
                  <div className="flex-1">
                    <label htmlFor="parental-consent" className="text-sm font-medium text-foreground cursor-pointer">
                      I am the Parent/Guardian and I Consent
                    </label>
                    <p className="text-xs text-muted-foreground mt-1">
                      I am the parent or legal guardian of the child who requested this account. 
                      I consent to the collection, use, and disclosure of my child's personal information 
                      as described in the Privacy Policy. I understand that I can revoke this consent at any time 
                      by contacting support or through the Parental Settings page.
                    </p>
                  </div>
                </div>
              </div>
            </ScrollArea>

            {/* Submit Button */}
            <div className="pt-4">
              <Button
                onClick={handleSubmit}
                disabled={!allChecked || isSubmitting}
                className="w-full"
                size="lg"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Verify Consent
                  </>
                )}
              </Button>
            </div>

            <p className="text-xs text-center text-muted-foreground">
              By clicking "Verify Consent", you acknowledge that you are the parent or legal guardian 
              and consent to allow your child to use Players Lockeroom.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Policy Viewer Dialog */}
      {viewingPolicy && (
        <PolicyDocumentViewer
          policyType={viewingPolicy}
          open={!!viewingPolicy}
          onClose={() => setViewingPolicy(null)}
        />
      )}
    </>
  );
};

export default VerifyParentalConsent;
